/**
 * MasterGraphView — BlocksPage 전용 Master Graph 렌더러.
 *
 * 역할은 **렌더 전용**: 필터(어떤 블럭을 보여줄지) 는 상위(`BlocksPage`) 가 결정해서
 * `nodes/edges` 로 넘긴다. 이 컴포넌트는 주어진 노드/엣지를 category 정적 원형 클러스터 +
 * 약한 force 보정으로 캔버스에 그릴 뿐이다.
 *
 * 설계 선택:
 *  - category 7종(actor/content/event/product/place/structure/decision) 정적 원형 앵커
 *    (`cluster-layout`). 블럭을 on/off 해도 좌표가 고정돼 사용자가 공간 기억을 유지.
 *  - 엣지는 기본 grayscale — hover 엣지만 incident 노드 카테고리 색으로 강조.
 *  - 엣지 relation 라벨은 hover 엣지에서만 렌더 (전역 globalScale 임계 없음).
 *  - 초기 mount 1회 자동 fitView → 이후엔 툴바 Fit 버튼만. RAF 재호출 없음.
 *  - `search` 텍스트가 있으면 비매칭 노드는 opacity 0.3 으로 흐림.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import ForceGraph2D, { type ForceGraphMethods } from "react-force-graph-2d"
import { buildAnchorMap, computeCategoryAnchors, initialNodePosition } from "@/shared/graph/cluster-layout.ts"
import type { GraphEdge, GraphNode } from "@/shared/graph/types.ts"
import { useMatchedIds } from "../../hooks/useTextFilter.ts"
import { buildCategoryColor, cssVar } from "../../utils/category-colors.ts"

const FALLBACK_COLOR_HEX = "#94a3b8"
const EDGE_IDLE_FALLBACK_HEX = "#94a3b8"
const LABEL_TEXT_FALLBACK = "#191f28"
const LABEL_BG_FALLBACK = "rgba(255,255,255,0.92)"
const NODE_LABEL_BG_FALLBACK = "rgba(255,255,255,0.9)"

/** 카테고리 앵커 배치 순서 (고정). */
const CATEGORY_ORDER = ["actor", "content", "event", "product", "place", "structure", "decision"] as const

/** 클러스터 반지름 — 노드 분포가 과도하게 퍼지지 않도록 중간값. */
const CLUSTER_RADIUS = 460
/** 노드 초기 jitter — 같은 카테고리 노드 군집 너비. */
const INITIAL_JITTER = 55

export interface MasterGraphHandle {
	fitView: () => void
}

export interface MasterGraphViewProps {
	readonly nodes: ReadonlyArray<GraphNode>
	readonly edges: ReadonlyArray<GraphEdge>
	readonly search?: string
	readonly onNodeSelect?: (node: GraphNode | null) => void
}

interface FgNode extends GraphNode {
	x?: number
	y?: number
}
interface FgLink {
	source: string | FgNode
	target: string | FgNode
	relation: string
}

function nodeCategory(n: GraphNode): string {
	return n.category ?? "structure"
}

function linkIncidentId(l: FgLink, side: "source" | "target"): string {
	const v = l[side]
	return typeof v === "string" ? v : (v?.id ?? "")
}

export const MasterGraphView = forwardRef<MasterGraphHandle, MasterGraphViewProps>(function MasterGraphView(
	{ nodes, edges, search = "", onNodeSelect },
	ref
) {
	const fgRef = useRef<ForceGraphMethods<FgNode, FgLink> | undefined>(undefined)
	const containerRef = useRef<HTMLDivElement>(null)
	const [containerSize, setContainerSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 })
	const [hoverNodeId, setHoverNodeId] = useState<string | undefined>()
	const hasFitRef = useRef(false)

	// Canvas 가 `var(--pk-*)` 를 직접 못 받으므로 mount 시 한 번 resolve
	const categoryColor = useMemo(() => buildCategoryColor(), [])
	const fallbackColor = useMemo(() => cssVar("--pk-gray-400") || FALLBACK_COLOR_HEX, [])
	const edgeIdleColor = useMemo(() => cssVar("--pk-gray-400") || EDGE_IDLE_FALLBACK_HEX, [])
	const canvasBg = useMemo(() => cssVar("--pk-bg-base") || "#ffffff", [])
	const labelText = useMemo(() => cssVar("--pk-text-primary") || LABEL_TEXT_FALLBACK, [])
	const linkLabelBg = useMemo(() => {
		// 라벨 배경은 canvas 위 텍스트 가독성을 위한 반투명 배경 — bg-base 가 토큰이지만
		// alpha 가 필요해 fallback 문자열을 그대로 사용 (디자인 토큰엔 alpha 변형이 없음).
		return LABEL_BG_FALLBACK
	}, [])
	const nodeLabelBg = useMemo(() => NODE_LABEL_BG_FALLBACK, [])
	const nodeStrokeColor = useMemo(() => cssVar("--pk-bg-base") || "#ffffff", [])

	// 컨테이너 크기 관찰 — canvas 치수 반응형
	useEffect(() => {
		if (!containerRef.current) return
		const el = containerRef.current
		const ro = new ResizeObserver(() => {
			const r = el.getBoundingClientRect()
			setContainerSize({ w: r.width, h: r.height })
		})
		ro.observe(el)
		const r = el.getBoundingClientRect()
		setContainerSize({ w: r.width, h: r.height })
		return () => ro.disconnect()
	}, [])

	// 카테고리 앵커 — 한 번만 계산 (CATEGORY_ORDER 고정)
	const anchorMap = useMemo(
		() => buildAnchorMap(computeCategoryAnchors([...CATEGORY_ORDER], CLUSTER_RADIUS)),
		[]
	)

	// 노드 초기 좌표 주입 — 결정적 jitter 로 category 중심 주변 분포.
	// 같은 블럭 id 는 항상 같은 좌표 → on/off 시에도 공간 기억 유지.
	const seededNodes: FgNode[] = useMemo(() => {
		return nodes.map((n) => {
			const p = initialNodePosition(anchorMap, n.id, nodeCategory(n), INITIAL_JITTER)
			return { ...n, x: p.x, y: p.y }
		})
	}, [nodes, anchorMap])

	// 검색 매칭 집합 — 흐림 처리에서 불투명 유지할 노드
	const pickNodeFields = useCallback((n: FgNode) => [n.name, n.kind, n.category ?? ""], [])
	const pickNodeId = useCallback((n: FgNode) => n.id, [])
	const matchedIds = useMatchedIds(seededNodes, search, pickNodeFields, pickNodeId)

	// hover 노드의 incident 엣지 집합
	const incidentEdgeKeys = useMemo(() => {
		if (!hoverNodeId) return undefined
		const keys = new Set<string>()
		for (const e of edges) {
			if (e.source === hoverNodeId || e.target === hoverNodeId) {
				keys.add(`${e.source}|${e.relation}|${e.target}`)
			}
		}
		return keys
	}, [hoverNodeId, edges])

	useImperativeHandle(
		ref,
		() => ({
			fitView: () => fgRef.current?.zoomToFit(400, 140)
		}),
		[]
	)

	// force 설정 — 약한 forceX/forceY 앵커 + collide. link/charge 는 보조적.
	useEffect(() => {
		const fg = fgRef.current
		if (!fg || containerSize.w === 0 || containerSize.h === 0) return
		void import("d3-force").then(({ forceCollide, forceX, forceY }) => {
			fg.d3Force("center")?.strength(0)
			fg.d3Force("charge")?.strength(-90).distanceMin(18).distanceMax(500)
			fg.d3Force("link")?.distance(70).strength(0.05)
			fg.d3Force("x", forceX((n: FgNode) => anchorMap.get(nodeCategory(n))?.x ?? 0).strength(0.5))
			fg.d3Force("y", forceY((n: FgNode) => anchorMap.get(nodeCategory(n))?.y ?? 0).strength(0.5))
			fg.d3Force("collide", forceCollide(26).strength(1).iterations(2))
		})
	}, [containerSize.w, containerSize.h, anchorMap])

	const hasNodes = seededNodes.length > 0

	return (
		<div
			ref={containerRef}
			className="master-graph-canvas"
			style={{
				position: "relative",
				width: "100%",
				height: "100%",
				minHeight: 520,
				overflow: "hidden"
			}}
		>
			{hasNodes && containerSize.w > 0 && containerSize.h > 0 && (
				<ForceGraph2D
					ref={fgRef}
					width={containerSize.w}
					height={containerSize.h}
					graphData={{ nodes: seededNodes, links: edges as unknown as FgLink[] }}
					backgroundColor={canvasBg}
					nodeId="id"
					nodeLabel={(n) => `${(n as FgNode).name}`}
					nodeRelSize={4}
					nodeVal={() => 2.2}
					warmupTicks={60}
					cooldownTicks={Number.POSITIVE_INFINITY}
					cooldownTime={2500}
					d3AlphaDecay={0.035}
					d3AlphaMin={0.01}
					d3VelocityDecay={0.35}
					autoPauseRedraw
					onEngineStop={() => {
						if (!hasFitRef.current) {
							fgRef.current?.zoomToFit(400, 140)
							hasFitRef.current = true
						}
					}}
					linkColor={(l) => {
						const fgl = l as FgLink
						const sid = linkIncidentId(fgl, "source")
						const tid = linkIncidentId(fgl, "target")
						const key = `${sid}|${fgl.relation}|${tid}`
						if (incidentEdgeKeys?.has(key)) {
							const src = seededNodes.find((n) => n.id === sid)
							return src ? (categoryColor[nodeCategory(src)] ?? fallbackColor) : fallbackColor
						}
						return edgeIdleColor
					}}
					linkWidth={(l) => {
						const fgl = l as FgLink
						const sid = linkIncidentId(fgl, "source")
						const tid = linkIncidentId(fgl, "target")
						const key = `${sid}|${fgl.relation}|${tid}`
						return incidentEdgeKeys?.has(key) ? 2.4 : 1.2
					}}
					linkDirectionalArrowLength={3.5}
					linkDirectionalArrowRelPos={1}
					linkCanvasObjectMode={() => "after"}
					linkCanvasObject={(link, ctx) => {
						const l = link as unknown as {
							source: FgNode & { x?: number; y?: number }
							target: FgNode & { x?: number; y?: number }
							relation: string
						}
						const sid = linkIncidentId(l as unknown as FgLink, "source")
						const tid = linkIncidentId(l as unknown as FgLink, "target")
						const key = `${sid}|${l.relation}|${tid}`
						if (!incidentEdgeKeys?.has(key)) return
						const src = l.source
						const tgt = l.target
						if (typeof src?.x !== "number" || typeof tgt?.x !== "number") return
						if (typeof src?.y !== "number" || typeof tgt?.y !== "number") return
						const mx = (src.x + tgt.x) / 2
						const my = (src.y + tgt.y) / 2
						ctx.font = "11px -apple-system, system-ui, sans-serif"
						ctx.textAlign = "center"
						ctx.textBaseline = "middle"
						const textW = ctx.measureText(l.relation).width
						ctx.fillStyle = linkLabelBg
						ctx.fillRect(mx - textW / 2 - 3, my - 7, textW + 6, 13)
						ctx.fillStyle = labelText
						ctx.fillText(l.relation, mx, my)
					}}
					nodeCanvasObject={(node, ctx, globalScale) => {
						const n = node as FgNode
						if (typeof n.x !== "number" || typeof n.y !== "number") return
						const cat = nodeCategory(n)
						const color = categoryColor[cat] ?? fallbackColor
						const isMatch = matchedIds?.has(n.id) ?? true
						const isHover = hoverNodeId === n.id
						const alpha = isHover || isMatch ? 1 : matchedIds ? 0.25 : 1
						ctx.globalAlpha = alpha
						ctx.beginPath()
						ctx.arc(n.x, n.y, 6, 0, Math.PI * 2)
						ctx.fillStyle = color
						ctx.fill()
						ctx.lineWidth = 1
						ctx.strokeStyle = nodeStrokeColor
						ctx.stroke()
						const label = n.name || n.id
						const fontSize = Math.max(11, Math.min(14, 12 / globalScale))
						ctx.font = `${fontSize}px -apple-system, system-ui, sans-serif`
						ctx.textAlign = "center"
						ctx.textBaseline = "top"
						const textW = ctx.measureText(label).width
						const ty = n.y + 9
						ctx.fillStyle = nodeLabelBg
						ctx.fillRect(n.x - textW / 2 - 3, ty - 1, textW + 6, fontSize + 3)
						ctx.fillStyle = labelText
						ctx.fillText(label, n.x, ty)
						ctx.globalAlpha = 1
					}}
					onNodeHover={(node) => {
						setHoverNodeId((node as FgNode | null)?.id)
					}}
					onNodeClick={(node) => {
						const n = node as FgNode
						onNodeSelect?.(n)
					}}
				/>
			)}
			{!hasNodes && (
				<div className="master-graph-empty">
					<div className="master-graph-empty__title">블럭을 켜 보세요</div>
					<ol className="master-graph-empty__steps">
						<li className="master-graph-empty__step">
							<span className="master-graph-empty__step-num">1</span>
							<span className="master-graph-empty__step-text">
								가운데 채팅에서 <em>"커머스 이벤트 추적 슬라이스 만들어줘"</em> 처럼 자연어로 요청하거나
							</span>
						</li>
						<li className="master-graph-empty__step">
							<span className="master-graph-empty__step-num">2</span>
							<span className="master-graph-empty__step-text">
								우측 상단의 <strong>"블럭 직접 켜기"</strong> 를 눌러 블럭을 하나씩 활성화하세요
							</span>
						</li>
						<li className="master-graph-empty__step">
							<span className="master-graph-empty__step-num">3</span>
							<span className="master-graph-empty__step-text">
								슬라이스 적용은 가운데 패널에서 <strong>대상 서비스</strong> 를 먼저 선택해야 활성화됩니다
							</span>
						</li>
					</ol>
				</div>
			)}
		</div>
	)
})
